import { MerchantOrderStatusEnum } from '@/graphql/generatedTypes';
import {
  CanAccessOperationsData,
  CanAccessPickingJobs,
  type Viewer,
} from '@/providers/ViewerContextProvider';
import { isReturnedToSender } from './orderFormPolicy';
import type { OrderKind } from './types';

interface LinkedPickingJob {
  id: string;
  resolution?: unknown;
}

/**
 * May this viewer follow the order's link to its picking job?
 *
 * `picking_job` resolves for every role that can load the order at all:
 * findOpsOrder and the field both require partner-data access, and every role
 * in CanAccessPickingJobs has it. So a null job means "no job", never "no
 * access". The page behind the link is the narrower gate — every other role
 * gets refused there — so the link follows CanAccessPickingJobs.
 */
export function canOpenOrderPickingJob<J extends LinkedPickingJob>(
  viewer: Viewer,
  pickingJob: J | null | undefined,
): pickingJob is J {
  return pickingJob != null && CanAccessPickingJobs(viewer);
}

export type PickingJobMenuItem = {
  jobId: string;
  action: 'resolve' | 'open';
} | null;

/**
 * The single picking-job entry in the order detail "⋮" menu.
 *
 * 'resolve' when ops has a decision to make on the job: an outbound order in
 * a terminal failure (ERROR, or RETURN_TO_SENDER ops has decided not to
 * replace) whose job is not resolved yet, for the roles that may resolve a
 * job. 'open' in every other case the viewer may open the job — a resolved
 * (released) job included, since it is the record of what happened.
 */
export function pickingJobMenuItem({
  viewer,
  orderKind,
  status,
  pickingJob,
}: {
  viewer: Viewer;
  orderKind: OrderKind;
  status?: MerchantOrderStatusEnum | null;
  pickingJob: LinkedPickingJob | null | undefined;
}): PickingJobMenuItem {
  if (!canOpenOrderPickingJob(viewer, pickingJob)) {
    return null;
  }
  const resolvable =
    CanAccessOperationsData(viewer) &&
    orderKind === 'outbound' &&
    pickingJob.resolution == null &&
    (status === MerchantOrderStatusEnum.Error || isReturnedToSender(status));
  return { jobId: pickingJob.id, action: resolvable ? 'resolve' : 'open' };
}
